/**
 * MainAnimation
 * 메인 페이지 전용 애니메이션 클래스
 */
export class MainAnimation {
    constructor() {
        this.advantageSection = null;
        this.advantageItems = [];
        this.advantageIndicators = [];
        this.observers = [];
        this.clientsLogosSwiper = null;
        this.heroTimeline = null;
        this.isTransitioning = false; // 솔루션 전환 중 여부
        this._scrollBlockers = null; // 스크롤 차단 리스너 참조
        this.logoMarqueeTimeline = null; // 로고 마퀴 타임라인
    }

    /**
     * 스크롤 임시 차단
     */
    disableScroll() {
        if (this._scrollBlockers) return;

        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };
        const preventKeys = (e) => {
            const keys = [
                "ArrowUp",
                "ArrowDown",
                "PageUp",
                "PageDown",
                "Home",
                "End",
                " ",
                "Spacebar",
            ];
            if (keys.includes(e.key) || keys.includes(e.code)) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };

        window.addEventListener("wheel", prevent, { passive: false });
        window.addEventListener("touchmove", prevent, { passive: false });
        window.addEventListener("keydown", preventKeys, { passive: false });
        this._scrollBlockers = { prevent, preventKeys };
    }

    /**
     * 스크롤 차단 해제
     */
    enableScroll() {
        if (!this._scrollBlockers) return;
        const { prevent, preventKeys } = this._scrollBlockers;
        window.removeEventListener("wheel", prevent, { passive: false });
        window.removeEventListener("touchmove", prevent, { passive: false });
        window.removeEventListener("keydown", preventKeys, { passive: false });
        this._scrollBlockers = null;
    }

    /**
     * 솔루션 전환 애니메이션 (대각선 클립패스)
     */
    transitionToSolution(items, dots, fromIndex, toIndex) {
        this.isTransitioning = true;
        this.disableScroll();
        const currentItem = items[fromIndex];
        const nextItem = items[toIndex];
        const tl = gsap.timeline();

        // 페이지네이션 업데이트
        dots.forEach((dot, index) => {
            dot.classList.toggle("is-active", index === toIndex);
        });

        // 현재 아이템 클래스 제거
        items.forEach((item) => item.classList.remove("is-active"));
        nextItem.classList.add("is-active");

        // 다음 아이템을 우측 상단에서 좌측 하단으로 대각선 클립패스
        tl.set(nextItem, {
            visibility: "visible",
            opacity: 1,
            clipPath: "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)",
            zIndex: 2,
        })
            .to(
                nextItem,
                {
                    clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
                    duration: 1.2,
                    ease: "power3.inOut",
                },
                0
            )
            .to(
                currentItem,
                {
                    clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)",
                    duration: 1.2,
                    ease: "power3.inOut",
                    onComplete: () => {
                        gsap.set(currentItem, {
                            visibility: "hidden",
                            opacity: 0,
                            zIndex: 1,
                        });
                        this.isTransitioning = false;
                        this.enableScroll();
                    },
                },
                0
            );
    }

    /**
     * Hero 섹션 클립패스 애니메이션
     */
    hero() {
        const heroSection = document.getElementById("hero-section");
        if (!heroSection) return;

        const videoElement = heroSection.querySelector(".no-hero__video");

        if (!videoElement) return;

        // 즉시 전체화면 영상: 스크롤 핀/클립 확장 애니메이션 없이
        // 영상은 CSS 기본값(클립 없음)으로 처음부터 화면을 꽉 채운다.
        // 타이틀/서브 문구는 영상과 겹쳐 지저분하여 CSS에서 숨김 처리(display:none).

        // YouTube iframe 지연 로딩: 초기 로드 무거움 완화
        this.scheduleHeroVideoLoad(videoElement);
    }

    /**
     * Hero YouTube iframe 지연 로딩 (data-src → src 주입)
     * 페이지 로드 시 iframe을 요청하지 않고, 타이틀 애니메이션 후 로드해 LCP/블로킹 완화
     */
    scheduleHeroVideoLoad(videoContainer) {
        if (!videoContainer) return;
        const iframe = videoContainer.querySelector(".no-hero__video-iframe");
        if (!iframe || !iframe.dataset.src) return;

        const load = () => {
            if (iframe.src) return;
            iframe.src = iframe.dataset.src;
        };

        // 영상이 즉시 전체화면으로 보이므로 로딩 지연을 짧게(초기 블로킹만 살짝 완화)
        const delay = 800;
        this.heroVideoLoadTimer = window.setTimeout(load, delay);
    }

    /**
     * Hero 텍스트 초기 애니메이션 (SplitType + GSAP)
     */
    initHeroTextAnimation(titleElement, titleLines) {
        if (!titleElement || !titleLines.length) return;

        // SplitType이 로드되었는지 확인
        if (typeof SplitType === "undefined") {
            console.warn(
                "MainAnimation: SplitType not found, using fallback animation"
            );
            this.fallbackTextAnimation(titleElement, titleLines);
            return;
        }

        // 각 title-line을 SplitType으로 분리
        const splitElements = [];
        titleLines.forEach((line, index) => {
            const split = new SplitType(line, {
                type: "chars,words",
                tagName: "span",
            });

            // 각 문자를 초기 상태로 설정
            gsap.set(split.chars, {
                opacity: 0,
                y: 50,
                rotationX: -90,
            });

            splitElements.push(split);
        });

        // frame도 초기 상태 설정
        const frameElement = document.querySelector(".no-hero__frame");
        if (frameElement) {
            gsap.set(frameElement, {
                opacity: 0,
                scale: 0.8,
                rotationX: -15,
            });
        }

        // title 전체 초기 상태
        gsap.set(titleElement, {
            opacity: 0,
        });

        // 애니메이션 타임라인 생성
        const textTimeline = gsap.timeline({
            delay: 0.3,
            onComplete: () => {
                // 애니메이션 완료 후 title 표시
                gsap.set(titleElement, {
                    opacity: 1,
                });
            },
        });

        // 첫 번째 라인 애니메이션
        if (splitElements[0] && splitElements[0].chars) {
            textTimeline.to(
                splitElements[0].chars,
                {
                    opacity: 1,
                    y: 0,
                    rotationX: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    stagger: {
                        amount: 0.6,
                        from: "start",
                    },
                },
                0
            );
        }

        // frame 애니메이션
        if (frameElement) {
            textTimeline.to(
                frameElement,
                {
                    opacity: 1,
                    scale: 1,
                    rotationX: 15,
                    duration: 1,
                    ease: "power3.out",
                },
                0.4
            );
        }

        // 두 번째 라인 애니메이션
        if (splitElements[1] && splitElements[1].chars) {
            textTimeline.to(
                splitElements[1].chars,
                {
                    opacity: 1,
                    y: 0,
                    rotationX: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    stagger: {
                        amount: 0.6,
                        from: "start",
                    },
                },
                0.8
            );
        }

        // title 전체 페이드 인
        textTimeline.to(
            titleElement,
            {
                opacity: 1,
                duration: 0.5,
                ease: "power2.out",
            },
            0.2
        );
    }

    /**
     * SplitType이 없을 때 사용하는 폴백 애니메이션
     */
    fallbackTextAnimation(titleElement, titleLines) {
        // title 전체 초기 상태
        gsap.set(titleElement, {
            opacity: 0,
            y: 30,
        });

        // frame 초기 상태
        const frameElement = document.querySelector(".no-hero__frame");
        if (frameElement) {
            gsap.set(frameElement, {
                opacity: 0,
                scale: 0.8,
            });
        }

        // 애니메이션 타임라인
        const textTimeline = gsap.timeline({
            delay: 0.3,
        });

        // 첫 번째 라인
        if (titleLines[0]) {
            textTimeline.from(
                titleLines[0],
                {
                    opacity: 0,
                    y: 50,
                    duration: 0.8,
                    ease: "power3.out",
                },
                0
            );
        }

        // frame
        if (frameElement) {
            textTimeline.from(
                frameElement,
                {
                    opacity: 0,
                    scale: 0.8,
                    duration: 1,
                    ease: "power3.out",
                },
                0.4
            );
        }

        // 두 번째 라인
        if (titleLines[1]) {
            textTimeline.from(
                titleLines[1],
                {
                    opacity: 0,
                    y: 50,
                    duration: 0.8,
                    ease: "power3.out",
                },
                0.8
            );
        }

        // title 전체
        textTimeline.to(
            titleElement,
            {
                opacity: 1,
                y: 0,
                duration: 0.5,
                ease: "power2.out",
            },
            0.2
        );
    }

    /**
     * Advantages 섹션 스크롤 트리거 초기화
     */
    advantage() {
        const section = document.getElementById("advantages-section");
        if (!section) return;

        // GSAP와 ScrollTrigger가 로드되었는지 확인
        if (
            typeof gsap === "undefined" ||
            typeof ScrollTrigger === "undefined"
        ) {
            console.warn("MainAnimation: GSAP or ScrollTrigger not found");
            return;
        }

        this.advantageSection = section;
        const sidebar = section.querySelector(".no-advantages__sidebar");
        const content = section.querySelector(".no-advantages__content");
        const items = section.querySelectorAll(".no-advantages__item");
        const indicators = section.querySelectorAll(
            ".no-advantages__indicator"
        );

        if (items.length === 0 || indicators.length === 0 || !sidebar) return;

        this.advantageItems = Array.from(items);
        this.advantageIndicators = Array.from(indicators);

        // 초기 첫 번째 항목 활성화
        this.setActiveItem(0);

        // matchMedia를 사용하여 PC에서만 ScrollTrigger 적용
        ScrollTrigger.matchMedia({
            // desktop (1025px 이상)
            "(min-width: 1025px)": () => {
                // ScrollTrigger로 사이드바 pin 설정
                // 콘텐츠 영역의 높이를 기준으로 스크롤 거리 계산
                const calculateScrollDistance = () => {
                    const viewportHeight = window.innerHeight;
                    const headerOffset = 120; // 헤더 높이 고려

                    // 콘텐츠의 실제 높이 (gap 포함)
                    const contentHeight = content.scrollHeight;
                    const sectionHeight = section.offsetHeight;

                    // 섹션 초기 높이 (뷰포트 기준, 헤더 제외)
                    const initialVisibleHeight = viewportHeight - headerOffset;

                    // 마지막 항목이 뷰포트 하단에 완전히 보이려면
                    // 콘텐츠가 스크롤되면서 마지막 항목의 하단이 뷰포트 하단에 오도록 해야 함
                    // 스크롤 거리 = 콘텐츠 전체 높이 - 초기 보이는 높이 + 여유 공간

                    if (contentHeight > initialVisibleHeight) {
                        // 마지막 항목이 완전히 보이도록 충분한 스크롤 거리
                        // contentHeight에서 초기 보이는 높이를 빼고, 뷰포트 높이의 40% 여유를 추가
                        // 이렇게 하면 마지막 항목이 위쪽에 잘리지 않고 완전히 보임
                        const scrollNeeded =
                            contentHeight - initialVisibleHeight;
                        return scrollNeeded + viewportHeight * 0.4;
                    }

                    // 콘텐츠가 짧은 경우: 최소 스크롤 거리
                    return Math.max(viewportHeight * 0.5, contentHeight * 0.5);
                };

                // 스크롤 거리 계산 및 pin 설정
                const initPin = () => {
                    const scrollDistance = calculateScrollDistance();

                    // 디버깅용 (필요시 주석 해제)
                    // console.log('Advantages Pin Debug:', {
                    //     contentHeight: content.scrollHeight,
                    //     sectionHeight: section.offsetHeight,
                    //     viewportHeight: window.innerHeight,
                    //     scrollDistance: scrollDistance,
                    //     itemsCount: items.length
                    // });

                    // scrollDistance가 너무 작으면 pin이 작동하지 않으므로 최소값 보장
                    // 하지만 너무 크면 빈 공간이 생기므로 정확히 계산한 값 사용
                    const finalScrollDistance = Math.max(
                        scrollDistance,
                        window.innerHeight * 0.5
                    );

                    // 기존 trigger가 있으면 제거
                    if (this.advantageScrollTrigger) {
                        this.advantageScrollTrigger.kill();
                    }

                    this.advantageScrollTrigger = ScrollTrigger.create({
                        trigger: section,
                        start: "top top",
                        end: `+=${finalScrollDistance}`,
                        pin: sidebar,
                        pinSpacing: true,
                        anticipatePin: 1,
                        markers: false,
                        invalidateOnRefresh: true,
                    });
                };

                // 이미지 로드 완료 후 정확한 높이 측정
                const waitForImages = () => {
                    const images = content.querySelectorAll("img");
                    let loadedCount = 0;
                    const totalImages = images.length;

                    if (totalImages === 0) {
                        // 이미지가 없으면 바로 초기화
                        ScrollTrigger.refresh();
                        initPin();
                        return;
                    }

                    const checkComplete = () => {
                        loadedCount++;
                        if (loadedCount === totalImages) {
                            // 모든 이미지 로드 완료 후 초기화
                            ScrollTrigger.refresh();
                            initPin();
                        }
                    };

                    images.forEach((img) => {
                        if (img.complete) {
                            checkComplete();
                        } else {
                            img.addEventListener("load", checkComplete);
                            img.addEventListener("error", checkComplete); // 에러 발생 시에도 진행
                        }
                    });
                };

                // 이미지 로드 대기
                waitForImages();

                // 리사이즈 대응
                let resizeTimer;
                const resizeHandler = () => {
                    clearTimeout(resizeTimer);
                    resizeTimer = setTimeout(() => {
                        // 리사이즈 시 모든 ScrollTrigger 갱신
                        ScrollTrigger.refresh();
                    }, 250);
                };
                window.addEventListener("resize", resizeHandler);

                // Intersection Observer 설정 (스크롤에 따른 활성 항목 변경)
                const observerOptions = {
                    root: null,
                    rootMargin: "-30% 0px -50% 0px", // 뷰포트 상단 30%, 하단 50% 지점에서 트리거
                    threshold: [0, 0.25, 0.5, 0.75, 1],
                };

                const observerCallback = (entries) => {
                    entries.forEach((entry) => {
                        const itemIndex = parseInt(
                            entry.target.getAttribute("data-item"),
                            10
                        );

                        if (
                            entry.isIntersecting &&
                            entry.intersectionRatio > 0.25
                        ) {
                            this.setActiveItem(itemIndex);
                        }
                    });
                };

                // 각 항목에 대해 Observer 생성
                this.advantageItems.forEach((item) => {
                    const observer = new IntersectionObserver(
                        observerCallback,
                        observerOptions
                    );
                    observer.observe(item);
                    this.observers.push(observer);
                });

                // 인디케이터 클릭 이벤트
                this.advantageIndicators.forEach((indicator, index) => {
                    indicator.addEventListener("click", () => {
                        this.scrollToItem(index);
                    });
                });
            },
        });
    }

    /**
     * 활성 항목 설정
     */
    setActiveItem(index) {
        if (index < 0 || index >= this.advantageItems.length) return;

        // 모든 항목과 인디케이터 비활성화
        this.advantageItems.forEach((item) => {
            item.classList.remove("is-active");
        });
        this.advantageIndicators.forEach((indicator) => {
            indicator.classList.remove("is-active");
        });

        // 선택된 항목과 인디케이터 활성화
        if (this.advantageItems[index]) {
            this.advantageItems[index].classList.add("is-active");
        }
        if (this.advantageIndicators[index]) {
            this.advantageIndicators[index].classList.add("is-active");
        }
    }

    /**
     * 특정 항목으로 스크롤
     */
    scrollToItem(index) {
        if (index < 0 || index >= this.advantageItems.length) return;

        const item = this.advantageItems[index];
        if (!item) return;

        const itemTop = item.getBoundingClientRect().top + window.pageYOffset;
        const offset = 200; // 헤더 높이 등 고려한 오프셋

        window.scrollTo({
            top: itemTop - offset,
            behavior: "smooth",
        });
    }

    /**
     * Global Network 섹션 애니메이션
     */
    network() {
        const networkSection = document.getElementById("network-section");
        if (!networkSection) return;

        // GSAP와 ScrollTrigger가 로드되었는지 확인
        if (
            typeof gsap === "undefined" ||
            typeof ScrollTrigger === "undefined"
        ) {
            console.warn("MainAnimation: GSAP or ScrollTrigger not found");
            return;
        }

        const mapBg = networkSection.querySelector(".no-network__map-bg");
        const overlay = networkSection.querySelector(".no-network__overlay");
        const header = networkSection.querySelector(".no-network__header");
        const stats = networkSection.querySelector(".no-network__stats");
        const statNumbers = networkSection.querySelectorAll(
            ".no-network__stat-number em"
        );

        if (!statNumbers || statNumbers.length === 0) return;

        // 공통 카운터업 함수 정의
        const startCounterUp = () => {
            statNumbers.forEach((statNumber, index) => {
                // data-value 속성에서 숫자 값 가져오기
                const parent = statNumber.parentElement;
                const dataValue = parent.getAttribute("data-value");
                const number = dataValue ? parseInt(dataValue) : 0;

                if (!isNaN(number) && number > 0) {
                    // 초기값을 0으로 설정
                    statNumber.textContent = "0";

                    // 순차적으로 카운터업 시작
                    setTimeout(() => {
                        const startTime = Date.now();
                        const duration = 1500;

                        const updateCounter = () => {
                            const elapsed = Date.now() - startTime;
                            const progress = Math.min(elapsed / duration, 1);

                            // easeOut 효과
                            const eased = 1 - Math.pow(1 - progress, 3);
                            const current = Math.floor(number * eased);

                            statNumber.textContent = current.toLocaleString();

                            if (progress < 1) {
                                requestAnimationFrame(updateCounter);
                            } else {
                                statNumber.textContent =
                                    number.toLocaleString();
                            }
                        };

                        updateCounter();
                    }, index * 150); // 순차적으로 카운터업 (150ms 간격)
                }
            });
        };

        if (!mapBg || !overlay || !header || !stats) return;

        // Background is always visible now (no mask / no pin).
        // Only reveal text + run counters when section approaches viewport.
        gsap.set([header, stats], { opacity: 0, y: 24 });

        ScrollTrigger.create({
            trigger: networkSection,
            start: "top 75%",
            once: true,
            onEnter: () => {
                networkSection.classList.add("is-inview");

                gsap.to(header, {
                    opacity: 1,
                    y: 0,
                    duration: 0.7,
                    ease: "power2.out",
                });

                gsap.to(stats, {
                    opacity: 1,
                    y: 0,
                    duration: 0.7,
                    ease: "power2.out",
                    delay: 0.12,
                    onStart: () => {
                        startCounterUp();
                    },
                });
            },
        });
    }

    /**
     * Logo Marquee 초기화 (GSAP 사용)
     */
    logoMarquee() {
        const marqueeSection = document.querySelector(".no-logo-marquee");
        if (!marqueeSection) return;

        const content = marqueeSection.querySelector(
            ".no-logo-marquee__content"
        );
        if (!content) return;

        // GSAP가 로드되었는지 확인
        if (typeof gsap === "undefined") {
            console.warn("MainAnimation: GSAP not found");
            return;
        }

        // 이미지 확인
        const images = content.querySelectorAll("img");
        if (images.length === 0) return;

        // 기존 애니메이션 제거
        if (this.logoMarqueeTimeline) {
            this.logoMarqueeTimeline.kill();
        }

        // 이미지 로드 확인
        let loadedCount = 0;
        const totalImages = images.length;

        const initMarquee = () => {
            // content의 전체 너비 계산
            const contentWidth = content.scrollWidth;

            // 로고들을 복제하여 무한 스크롤 효과 생성
            const clone = content.cloneNode(true);
            clone.classList.add("no-logo-marquee__content--clone");
            content.parentElement.appendChild(clone);

            // 초기 위치 설정 - 겹치지 않게 원본 바로 뒤에 배치
            gsap.set(content, { x: 0 });
            gsap.set(clone, { x: contentWidth });

            // 속도 계산 (50px/s)
            const speed = 50;
            const duration = contentWidth / speed;

            // 일시정지 플래그
            let isPaused = false;
            let currentAnimation = null;

            // 애니메이션 생성 (끊김 없이 무한 반복)
            const animate = () => {
                if (isPaused) return; // 일시정지 중이면 실행하지 않음

                // 원본과 클론을 동시에 왼쪽으로 이동
                currentAnimation = gsap.to([content, clone], {
                    x: `-=${contentWidth}`,
                    duration: duration,
                    ease: "none",
                    onComplete: () => {
                        // 한 사이클 완료 시 위치 조정
                        const contentX = parseFloat(
                            gsap.getProperty(content, "x")
                        );
                        const cloneX = parseFloat(gsap.getProperty(clone, "x"));

                        // 첫 번째가 완전히 사라지면 뒤로 이동 (끊김 없이)
                        if (contentX <= -contentWidth) {
                            gsap.set(content, { x: cloneX + contentWidth });
                        }
                        if (cloneX <= -contentWidth) {
                            gsap.set(clone, { x: contentX + contentWidth });
                        }

                        // 계속 반복 (끊김 없이)
                        animate();
                    },
                });
            };

            // 호버 시 일시 정지
            marqueeSection.addEventListener("mouseenter", () => {
                if (!isPaused) {
                    isPaused = true;
                    if (currentAnimation) {
                        currentAnimation.pause();
                    }
                }
            });

            marqueeSection.addEventListener("mouseleave", () => {
                if (isPaused) {
                    isPaused = false;
                    if (currentAnimation) {
                        currentAnimation.resume();
                    } else {
                        // 애니메이션이 없으면 다시 시작
                        animate();
                    }
                }
            });

            // 애니메이션 시작
            this.logoMarqueeTimeline = {
                pause: () => {
                    isPaused = true;
                    if (currentAnimation) currentAnimation.pause();
                },
                resume: () => {
                    isPaused = false;
                    if (currentAnimation) {
                        currentAnimation.resume();
                    } else {
                        animate();
                    }
                },
                kill: () => {
                    isPaused = true;
                    if (currentAnimation) {
                        currentAnimation.kill();
                        currentAnimation = null;
                    }
                },
            };

            // 애니메이션 시작
            animate();
        };

        const checkImageLoad = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
                // 모든 이미지 로드 완료 후 초기화
                initMarquee();
            }
        };

        // 이미지 로드 상태 확인
        images.forEach((img) => {
            if (img.complete && img.naturalHeight !== 0) {
                checkImageLoad();
            } else {
                img.addEventListener("load", checkImageLoad, { once: true });
                img.addEventListener("error", () => {
                    console.warn(
                        "MainAnimation: Image failed to load",
                        img.src
                    );
                    checkImageLoad();
                });
            }
        });

        // 모든 이미지가 이미 로드된 경우
        if (loadedCount === totalImages) {
            initMarquee();
        }
    }

    /**
     * Our Partners(클라이언트 로고) Swiper — 10개씩 묶어 슬라이드
     */
    clientsLogos() {
        const el = document.getElementById("clients-logos-swiper");
        if (!el || typeof Swiper === "undefined") return;

        this.clientsLogosSwiper = new Swiper("#clients-logos-swiper", {
            slidesPerView: 1,
            spaceBetween: 0,
            speed: 1000,
            autoplay: {
                delay: 4000,
                disableOnInteraction: false,
            },
            pagination: {
                el: ".no-clients-logos__pagination",
                type: "bullets",
                clickable: true,
            },
        });
    }

    /**
     * 정리
     */
    destroy() {
        this.observers.forEach((observer) => {
            observer.disconnect();
        });
        this.observers = [];

        if (this.clientsLogosSwiper) {
            this.clientsLogosSwiper.destroy(true, true);
            this.clientsLogosSwiper = null;
        }

        if (this.heroVideoLoadTimer) {
            window.clearTimeout(this.heroVideoLoadTimer);
            this.heroVideoLoadTimer = null;
        }
        if (this.heroTimeline) {
            this.heroTimeline.kill();
            this.heroTimeline = null;
        }

        if (this.logoMarqueeTimeline) {
            this.logoMarqueeTimeline.kill();
            this.logoMarqueeTimeline = null;
        }

        // ScrollTrigger 전체 정리
        if (typeof ScrollTrigger !== "undefined") {
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        }
    }
}
